import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { fetchProjectLookup } from '@/lib/entity-api';
import { useEntityList } from '@/hooks/useEntityList';
import { exportEntityCsv } from '@/lib/csv';
import { Copy, Trash2, Download, ArrowUpDown } from 'lucide-react';
import './Selections.css';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton, SaveChangesButton, CancelButton } from '@/styles/StandardButtons';
import { GenericTag } from '@/styles/GenericTag';
import { GenericTabBar } from '@/styles/GenericTabBar';
import { GenericKPIDashboard, type KPIItem } from '@/styles/GenericKPIDashboard';
import { useUser } from '@/contexts/UserContext';
import { useProject } from '@/components/ProjectSelector';
import { SelectionForm } from './SelectionForm';
import { useOpenEntityRecord } from '@/hooks/useOpenEntity';
import { SelectionCardView } from './SelectionCardView';
import type { OptionCardData } from './SelectionOptionCard';
import {
  fetchSelectionsOverview, fetchSelectionsPage, exportSelectionRows, setOptionSelected, deleteSelections, duplicateSelection, saveSelectionOrder, mergeSubsetOrder, fetchSelectionById,
  type Selection, type SelectionsSummary, type SelectionsOverview,
} from './store';

type ViewMode = 'list' | 'card';
const VIEW_TABS = [
  { value: 'list', label: 'List View' },
  { value: 'card', label: 'Card View' },
];

const EMPTY_SUMMARY: SelectionsSummary = { total: 0, selected: 0, pending: 0, client_total: 0 };

export function SelectionsList() {
  const { companyId } = useUser();
  const { selectedProject } = useProject();
  // The Project Selector filters everything: list, cards and the KPI bar come from one scoped request.
  const projectFilter = selectedProject && selectedProject !== 'all' ? selectedProject : undefined;
  const [view, setView] = useState<ViewMode>('list');
  // The list: ONE server-driven query (project + search + sort + page) shared by List View and Card View -- both show the
  // same rows, search / sort / paging run in the database over every selection, and the KPI bar describes ALL of them.
  const list = useEntityList<Selection, Selection>({
    entityType: 'selection',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'append',
    pageSize: 20,
    mapRow: (row) => row,
    fetchPage: fetchSelectionsPage,
  });
  const selections = list.items;
  const overview = list.pageExtra as { summary: SelectionsSummary; capabilities: SelectionsOverview['capabilities'] } | undefined;
  const summary = overview?.summary ?? EMPTY_SUMMARY;
  const canViewBuilderPrice = overview?.capabilities.view_builder_price ?? false;
  const refresh = list.refresh;
  // Cards re-sync their local arrangement whenever the server data changes.
  const [dataVersion, setDataVersion] = useState(0);
  useEffect(() => { setDataVersion((v) => v + 1); }, [list.items]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentSelection, setCurrentSelection] = useState<Selection | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyOptionId, setBusyOptionId] = useState<string | null>(null);
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  // Reorder mode: the order the user is arranging (ids), not saved until Save Changes.
  const [draftOrder, setDraftOrder] = useState<string[] | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const reorderMode = draftOrder !== null;

  useEffect(() => {
    // A different project / search / sort is a new list: what was selected or being arranged belongs to the old one.
    setDraftOrder(null);
    setSelectedRows([]);
  }, [list.queryKey]);

  useEffect(() => {
    if (!companyId) return;
    fetchProjectLookup().then(setProjects).catch((err) => console.error('Error fetching projects:', err));
  }, [companyId]);

  const getProjectName = useCallback((projectId: string) => projects.find((p) => p.id === projectId)?.name || projectId, [projects]);

  // Reorder mode arranges EVERY selection of the project (all of them, in the manual order), whatever page / search is showing.
  const [reorderRows, setReorderRows] = useState<Selection[]>([]);
  const filteredData = useMemo(() => {
    if (draftOrder) {
      const byId = new Map(reorderRows.map((s) => [s.id, s]));
      return draftOrder.map((id) => byId.get(id)).filter((s): s is Selection => !!s);
    }
    return selections;
  }, [selections, draftOrder, reorderRows]);

  const startReorder = async () => {
    setActionError(null);
    try {
      const all = (await fetchSelectionsOverview(projectFilter)).selections;
      setReorderRows(all);
      setDraftOrder(all.map((s) => s.id));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to load the selections to reorder');
    }
  };
  const cancelReorder = () => setDraftOrder(null);
  // ONE request with the final order. On failure the user's arrangement is kept
  // (still in reorder mode) and the error is shown -- nothing is lost silently.
  const saveReorder = async () => {
    if (!draftOrder) return;
    setSavingOrder(true);
    setActionError(null);
    try {
      // With a project filter only part of the list was arranged: merge it into the
      // company's full order (one extra read), then persist everything in ONE request.
      const fullIds = projectFilter ? (await fetchSelectionsOverview()).selections.map((x) => x.id) : draftOrder;
      await saveSelectionOrder(projectFilter ? mergeSubsetOrder(fullIds, draftOrder) : draftOrder);
      await refresh();
      setDraftOrder(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save the order');
    } finally {
      setSavingOrder(false);
    }
  };

  const openNew = () => { setCurrentSelection(null); setIsFormOpen(true); };
  const openEdit = (selection: Selection) => { setCurrentSelection(selection); setIsFormOpen(true); };
  // a link from another document (Linked Docs) opens this selection
  useOpenEntityRecord('selection', fetchSelectionById, openEdit);

  // Approve / un-approve (Card View): saved through the API on every click --
  // no Save button, no optimistic guess. The server returns the truth
  // (including what it did to the sibling options) and the page reloads.
  const handleToggleOption = async (selection: Selection, option: OptionCardData) => {
    setBusyOptionId(option.id);
    setActionError(null);
    try {
      await setOptionSelected(selection.id, option.id, !option.is_selected);
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update the selection');
    } finally {
      setBusyOptionId(null);
    }
  };

  const handleCopySelected = async () => {
    if (selectedRows.length === 0) { alert('Please select at least one selection to copy.'); return; }
    try {
      // The server copies everything (options, links, prices, files); the copy starts unresolved.
      let attachmentsFailed = 0;
      for (const selectionId of selectedRows) attachmentsFailed += (await duplicateSelection(selectionId)).attachmentsFailed;
      await refresh();
      setSelectedRows([]);
      alert(attachmentsFailed > 0 ? `Copied ${selectedRows.length} selection(s); ${attachmentsFailed} file(s) could not be copied.` : `Successfully copied ${selectedRows.length} selection(s).`);
    } catch (err) {
      console.error('Error copying selections:', err);
      alert('Error copying selections. Please try again.');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) { alert('Please select at least one selection to delete.'); return; }
    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} selection(s)? This action cannot be undone.`)) return;
    try {
      const { deleted, failed } = await deleteSelections(selectedRows);
      await refresh();
      setSelectedRows([]);
      alert(failed > 0 ? `Deleted ${deleted} selection(s); ${failed} could not be deleted.` : `Successfully deleted ${deleted} selection(s).`);
    } catch (err) {
      console.error('Error deleting selections:', err);
      alert('Error deleting selections. Please try again.');
    }
  };

  // Export to CSV: the rows selected in the table, or -- with nothing selected -- EVERY selection of the current project +
  // search, read by the backend with its derived columns (status, selected totals, counters; Builder Price only if allowed).
  const handleExportSelected = () => exportEntityCsv(
    async (ids) => (await exportSelectionRows(list.query, ids)).map((row) => Object.fromEntries(
      Object.entries(row).map(([column, value]) => (column === 'project_id' ? ['project', getProjectName(value as string)] : [column, value]))
    )),
    selectedRows, 'selections', 'selections'
  );

  const formatSelectionNumber = (selNum: string | number): string => {
    if (typeof selNum === 'number') return `SE${selNum.toString().padStart(6, '0')}`;
    return selNum || '-';
  };

  const handleSaved = async ({ failedFiles }: { selectionId: string; failedFiles: number }) => {
    setIsFormOpen(false);
    setCurrentSelection(null);
    await refresh();
    if (failedFiles > 0) alert(`The selection was saved, but ${failedFiles} file(s) could not be uploaded. Open the option and upload them again.`);
  };

  // Refresh the open form's selection from the new data while it stays open.
  const openSelection = currentSelection ? selections.find((s) => s.id === currentSelection.id) ?? currentSelection : null;

  // Define table columns (placed after all handler functions to avoid hoisting issues)
  const columns: GenericTableColumn<Selection>[] = [
    {
      key: 'selection_number',
      header: 'Selection ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'selection_number',
      render: (row) => <span className="font-medium">{formatSelectionNumber(row.selection_number)}</span>,
    },
    {
      key: 'name',
      header: 'Title',
      width: '10rem',
      sortable: true,
      sortKey: 'name',
      render: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      key: 'project_id',
      header: 'Project',
      width: '6.5rem',
      sortable: true,
      sortKey: 'project',
      render: (row) => getProjectName(row.project_id),
    },
    {
      key: 'status',
      header: 'Status',
      width: '6.5rem',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={row.status}
        />
      ),
    },
    {
      key: 'allowance',
      header: 'Allowance',
      width: '8rem',
      sortable: true,
      sortKey: 'allowance',
      render: (row) => (
        <div className="allowance-display">
          <div className="allowance-ratio">
            {row.selected_client_price ? `$${row.selected_client_price.toLocaleString()}` : '$0'}/${row.allowance.toLocaleString()}
          </div>
          {row.allowance > 0 && (
            <div className="allowance-progress">
              <div 
                className="allowance-progress-bar" 
                style={{ 
                  width: `${row.selected_client_price ? Math.min((row.selected_client_price / row.allowance) * 100, 100) : 0}%`,
                  backgroundColor: row.selected_client_price
                    ? (row.selected_client_price <= row.allowance ? 'rgb(var(--indicator-good))' : 'rgb(var(--indicator-bad))')
                    : 'rgb(var(--gray-200))'
                }}
              ></div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'due_date',
      header: 'Due Date',
      width: '6rem',
      sortable: true,
      sortKey: 'due_date',
      render: (row) => row.due_date ? new Date(row.due_date).toLocaleDateString() : '-',
    },
    {
      key: 'attachment_count',
      header: 'Attachments',
      width: '6.25rem',
      render: (row) => <span>{row.attachment_count || 0}</span>,
    },
    {
      key: 'comment_count',
      header: 'Comments',
      width: '6.25rem',
      render: (row) => <span>{row.comment_count || 0}</span>,
    },
  ];

  const menuItems = [
    ...(view === 'card'
      ? [{ label: 'Reorder', icon: <ArrowUpDown className="w-4 h-4" />, onClick: startReorder, disabled: list.total < 2 }]
      : []),
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  // Until the first answer of the server nothing is known - not the figures, and not whether this caller may see the Builder Total - so the bar shows its four tiles
  // as placeholders from the first render (it used to show three zeros and then add the fourth when the data arrived).
  const kpisLoading = overview === undefined;
  const kpis: KPIItem[] = [
    { key: 'selected', label: 'Selected', value: `${summary.selected}/${summary.total}`, loading: kpisLoading },
    { key: 'pending', label: 'Pending', value: summary.pending, format: 'number', loading: kpisLoading },
    { key: 'client_total', label: 'Client Total', value: summary.client_total, format: 'currency', loading: kpisLoading },
    // The server only sends builder_total to callers allowed to see it.
    { key: 'builder_total', label: 'Builder Total', value: summary.builder_total, format: 'currency', loading: kpisLoading },
  ];

  if (list.error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">Error loading selections. Please try again.</p>
        <Button onClick={() => window.location.reload()}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <GenericTabBar tabs={VIEW_TABS} activeTab={view} onTabChange={(value) => { if (!reorderMode) setView(value as ViewMode); }} />
        {reorderMode ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Drag the handles to reorder</span>
            <CancelButton onClick={cancelReorder} disabled={savingOrder}>Cancel</CancelButton>
            <SaveChangesButton type="button" onClick={saveReorder} disabled={savingOrder}>{savingOrder ? 'Saving…' : 'Save Changes'}</SaveChangesButton>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <NewItemButton label="New" onClick={openNew} />
            <SearchButton onSearch={list.setSearchInput} placeholder="Search" />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        )}
      </div>

      <GenericKPIDashboard items={kpis} />

      {actionError && <p className="text-sm text-red-600" role="alert">{actionError}</p>}

      {view === 'list' ? (
        <GenericTable
          columns={columns}
          {...list.tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={1200}
          onRowClick={openEdit}
          selectable={true}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
          emptyState="No selections found"
        />
      ) : (
        <div className="space-y-4">
        <SelectionCardView
          selections={filteredData}
          projectName={getProjectName}
          onToggleOption={handleToggleOption}
          onOpenSelection={openEdit}
          busyOptionId={busyOptionId}
          dataVersion={dataVersion}
          loading={list.isLoading && !reorderMode}
          reorderMode={reorderMode}
          onOrderChange={setDraftOrder}
        />
        {!reorderMode && list.hasMore && (
          <div className="flex justify-center">
            <Button variant="outline" onClick={list.loadMore} disabled={list.isLoading}>{list.isLoading ? 'Loading…' : 'Load more'}</Button>
          </div>
        )}
        </div>
      )}

      <SelectionForm
        mode={openSelection ? 'edit' : 'create'}
        initialData={openSelection || undefined}
        projects={projects}
        canViewBuilderPrice={canViewBuilderPrice}
        onSaved={handleSaved}
        onSelectionChanged={refresh}
        onCancel={() => { setIsFormOpen(false); setCurrentSelection(null); }}
        isOpen={isFormOpen}
      />
    </div>
  );
}
